import { useState, useEffect, useCallback } from "react";

import { getBrand } from "@/shared/brand/store";
import { applyBrandOverlay } from "@/shared/brand/themeOverlay";

export type ThemeKey = "cyber" | "comic" | "standard";

interface ThemeTokens {
  label: string;
  description: string;
  preview: string; // primary swatch
  accent: string; // accent swatch
  surface: string; // background swatch
  /**
   * CSS vars set on :root when this skin is active.
   * The heavy cosmetic overrides (cards/buttons/sidebar/dialog…)
   * live in index.css under `[data-skin="<key>"]` selectors.
   */
  vars: Record<string, string>;
}

export type ThemeOption = ThemeTokens & { key: ThemeKey };

const themes: Record<ThemeKey, ThemeTokens> = {
  cyber: {
    label: "霓虹科技",
    description: "深空玻璃 · 霓虹扫描 · 赛博 HUD",
    preview: "hsl(186 100% 55%)",
    accent: "hsl(275 95% 65%)",
    surface: "hsl(230 40% 5%)",
    vars: {
      "--background": "230 40% 5%",
      "--foreground": "200 35% 96%",
      // 卡面抬升(2026-07-25 发现流):9%→14% 让卡从页底(5%)浮出;真源在此(applyTheme 内联,盖过 CSS)。
      "--card": "230 36% 14%",
      "--card-foreground": "200 35% 96%",
      "--popover": "230 36% 15%",
      "--popover-foreground": "200 35% 96%",
      "--primary": "186 100% 55%",
      "--primary-foreground": "230 60% 6%",
      "--secondary": "230 35% 15%",
      "--secondary-foreground": "200 35% 92%",
      "--muted": "230 30% 13%",
      // 正文提亮 + 降蓝(70%→80% L,饱和 25%→22%):低饱和蓝灰压蓝黑底发糊,提亮去糊。
      "--muted-foreground": "205 22% 80%",
      "--accent": "275 85% 20%",
      "--accent-foreground": "280 100% 85%",
      "--destructive": "342 92% 60%",
      "--destructive-foreground": "0 0% 100%",
      "--success": "152 85% 55%",
      "--success-foreground": "230 60% 6%",
      "--warning": "42 100% 60%",
      "--warning-foreground": "230 60% 6%",
      "--info": "200 100% 60%",
      "--info-foreground": "230 60% 6%",
      // 边缘光加实(0.22→0.36):cyber 的分层语言是霓虹描边,把卡"框"出来。
      "--border": "200 85% 58% / 0.36",
      "--input": "230 35% 16%",
      "--ring": "186 100% 55%",
      "--radius": "0.75rem",
      "--sidebar-background": "230 50% 6%",
      "--sidebar-foreground": "200 35% 90%",
      "--sidebar-primary": "186 100% 55%",
      "--sidebar-primary-foreground": "230 60% 6%",
      "--sidebar-accent": "200 100% 55% / 0.14",
      "--sidebar-accent-foreground": "186 100% 85%",
      "--sidebar-border": "200 100% 55% / 0.18",
      "--sidebar-ring": "186 100% 55%",
      "--sidebar-muted": "200 25% 55%",
    },
  },
  comic: {
    label: "漫画可爱",
    description: "奶油糖果 · 粗黑边 · 气泡阴影",
    preview: "hsl(335 85% 65%)",
    accent: "hsl(48 100% 60%)",
    surface: "hsl(35 100% 96%)",
    vars: {
      "--background": "35 100% 96%",
      "--foreground": "260 30% 18%",
      "--card": "0 0% 100%",
      "--card-foreground": "260 30% 18%",
      "--popover": "0 0% 100%",
      "--popover-foreground": "260 30% 18%",
      "--primary": "335 85% 65%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "200 90% 88%",
      "--secondary-foreground": "210 50% 22%",
      "--muted": "35 70% 92%",
      "--muted-foreground": "260 20% 45%",
      "--accent": "48 100% 75%",
      "--accent-foreground": "30 50% 22%",
      "--destructive": "0 85% 65%",
      "--destructive-foreground": "0 0% 100%",
      "--success": "150 65% 55%",
      "--success-foreground": "0 0% 100%",
      "--warning": "38 100% 58%",
      "--warning-foreground": "30 50% 18%",
      "--info": "200 90% 60%",
      "--info-foreground": "0 0% 100%",
      "--border": "260 30% 18%",
      "--input": "0 0% 100%",
      "--ring": "335 85% 65%",
      "--radius": "1.25rem",
      "--sidebar-background": "200 90% 90%",
      "--sidebar-foreground": "260 30% 18%",
      "--sidebar-primary": "335 85% 65%",
      "--sidebar-primary-foreground": "0 0% 100%",
      "--sidebar-accent": "48 100% 78%",
      "--sidebar-accent-foreground": "30 50% 22%",
      "--sidebar-border": "260 30% 18%",
      "--sidebar-ring": "335 85% 65%",
      "--sidebar-muted": "260 20% 45%",
    },
  },
  standard: {
    label: "中规中矩",
    // Stripe-flavoured: blurple primary + soft mesh background + tiered separators
    description: "克制专业 · Stripe 调色板 · 细腻分隔",
    preview: "hsl(244 100% 68%)",
    accent: "hsl(262 100% 72%)",
    surface: "hsl(0 0% 100%)",
    vars: {
      "--background": "210 50% 99%",
      "--foreground": "213 70% 12%",
      "--card": "0 0% 100%",
      "--card-foreground": "213 70% 12%",
      "--popover": "0 0% 100%",
      "--popover-foreground": "213 70% 12%",
      "--primary": "244 100% 68%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "240 25% 96%",
      "--secondary-foreground": "213 70% 18%",
      "--muted": "220 30% 96%",
      "--muted-foreground": "215 20% 42%",
      "--accent": "262 100% 96%",
      "--accent-foreground": "262 65% 32%",
      "--destructive": "354 84% 56%",
      "--destructive-foreground": "0 0% 100%",
      "--success": "159 70% 38%",
      "--success-foreground": "0 0% 100%",
      "--warning": "32 100% 50%",
      "--warning-foreground": "0 0% 100%",
      "--info": "210 100% 56%",
      "--info-foreground": "0 0% 100%",
      "--border": "220 20% 91%",
      "--input": "220 20% 91%",
      "--ring": "244 100% 68%",
      "--radius": "0.5rem",
      "--sidebar-background": "210 50% 99%",
      "--sidebar-foreground": "213 60% 22%",
      "--sidebar-primary": "244 100% 68%",
      "--sidebar-primary-foreground": "0 0% 100%",
      "--sidebar-accent": "244 100% 96%",
      "--sidebar-accent-foreground": "244 60% 32%",
      "--sidebar-border": "220 20% 92%",
      "--sidebar-ring": "244 100% 68%",
      "--sidebar-muted": "215 20% 48%",
    },
  },
};

const STORAGE_KEY = "xbgeo-theme";

function readInitial(): ThemeKey {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw && raw in themes) return raw as ThemeKey;
    return "cyber";
  } catch {
    return "cyber";
  }
}

/**
 * 应用皮肤(模块级,无 hook 依赖)— 供 usePreferenceSync 在登录后用服务端 theme 覆盖时调用,
 * 不必持有某个组件的 useTheme 实例。同时写 localStorage 让下次启动即时生效、不闪。
 */
export function applyTheme(key: ThemeKey) {
  const tokens = themes[key];
  if (!tokens) return;
  const root = document.documentElement;
  root.setAttribute("data-skin", key);
  Object.entries(tokens.vars).forEach(([prop, val]) => {
    root.style.setProperty(prop, val);
  });
  // OEM 贴牌(C-E):皮肤落定后套品牌主色覆盖层(只换色相/饱和度,亮度随皮肤)。
  // 品牌没配主色(直营永远如此)= 覆盖层什么都不做。
  applyBrandOverlay(getBrand(), root);
  try {
    localStorage.setItem(STORAGE_KEY, key);
  } catch {
    // localStorage 不可用时静默忽略
  }
}

/**
 * 启动同步应用主题(在 render 前调,main.tsx)。此前主题只由 usePreferenceSync(登录后异步)
 * 与 ProfilePage 应用 → 同步前显 :root 默认、同步后翻转 = 「点页面突然换主题」(UI-CONSIST-07)。
 * 启动即按 localStorage 落定 data-skin + 内联变量,消除首帧翻转;usePreferenceSync 仍负责跨设备覆盖。
 */
export function initTheme() {
  applyTheme(readInitial());
}

/**
 * 订阅**当前生效**的皮肤(读 DOM 上的 data-skin,MutationObserver 跟随)。
 *
 * <p>为什么不用 useTheme():它每个实例各持一份 useState —— 设置页里切皮肤只更新它自己那份,
 * 别处的 useTheme() 实例读到的仍是挂载时的旧值(applyTheme 的 DOM 副作用是全局的,React 状态不是)。
 * 而 data-skin 由 applyTheme 统一写,initTheme / 设置页 / usePreferenceSync 服务端覆盖三条路都覆盖得到,
 * 所以它才是「当前皮肤」的单一真相源。只读,不参与切换。
 */
export function useActiveSkin(): ThemeKey {
  const [skin, setSkin] = useState<ThemeKey>(() =>
    typeof document === "undefined" ? "cyber" : readActiveSkin()
  );
  useEffect(() => {
    const root = document.documentElement;
    const sync = () => setSkin(readActiveSkin());
    sync(); // 挂载时补一次:初值取自首帧,期间 initTheme 可能已改
    const mo = new MutationObserver(sync);
    mo.observe(root, { attributes: true, attributeFilter: ["data-skin"] });
    return () => mo.disconnect();
  }, []);
  return skin;
}

function readActiveSkin(): ThemeKey {
  const v = document.documentElement.getAttribute("data-skin");
  return v && v in themes ? (v as ThemeKey) : "cyber";
}

export function useTheme() {
  const [theme, setThemeState] = useState<ThemeKey>(readInitial);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback((key: ThemeKey) => {
    setThemeState(key);
    try {
      localStorage.setItem(STORAGE_KEY, key);
    } catch {
      // localStorage may be unavailable (private mode, SSR, …) — silently ignore.
    }
  }, []);

  return {
    theme,
    setTheme,
    themes,
    themeList: Object.entries(themes).map(([k, v]) => ({ key: k as ThemeKey, ...v })),
  };
}
